/**
 * ApiDocs.jsx — v2 widget that renders the static OpenAPI-style spec
 * returned by `/api/v2/widgets/api-docs` (anon-tier; same payload as
 * the public `/api/docs` route).
 *
 * Issue: #253
 *
 * CSP: all interactivity uses Solid `onClick` (compiled to
 * addEventListener). No inline event handlers, no dangerouslySetInnerHTML.
 */
import { Show, For, createMemo, createSignal } from 'solid-js';
import useWidgetData from './useWidgetData.js';

function methodColour(m) {
  switch ((m || '').toUpperCase()) {
    case 'GET':    return 'var(--t-green)';
    case 'POST':   return 'var(--t-cyan)';
    case 'DELETE': return 'var(--bad, #ff6b6b)';
    case 'PATCH':  return 'var(--gold, #ffc857)';
    case 'PUT':    return 'var(--gold, #ffc857)';
    default:       return 'var(--t-dim)';
  }
}

// Issue #301 — surface the HTTP verb's intent on hover so a casual
// reader learns the verb without leaving the dashboard.
function methodHint(m) {
  switch ((m || '').toUpperCase()) {
    case 'GET':    return 'GET — read-only fetch (idempotent, never mutates state)';
    case 'POST':   return 'POST — create or trigger an action (not idempotent)';
    case 'DELETE': return 'DELETE — remove a resource (idempotent)';
    case 'PATCH':  return 'PATCH — partial update of an existing resource';
    case 'PUT':    return 'PUT — full replacement of a resource (idempotent)';
    default:       return 'HTTP method';
  }
}

// Issue #301 — colour-code auth tier the same way methods are coded so
// the eye picks up access requirements at a glance.
function authColour(a) {
  const v = String(a || '').toLowerCase();
  if (v.includes('admin'))  return 'var(--bad, #ff6b6b)';
  if (v.includes('user') || v.includes('session') || v.includes('discord')) return 'var(--gold, #ffc857)';
  if (v.includes('anon') || v.includes('public') || v.includes('none')) return 'var(--t-green)';
  return 'var(--t-dim)';
}

function authHint(a) {
  const v = String(a || '').toLowerCase();
  if (v.includes('admin'))  return 'Admin only — request must carry an admin session.';
  if (v.includes('user') || v.includes('session') || v.includes('discord')) return 'Authenticated user — Discord OAuth session required.';
  if (v.includes('anon') || v.includes('public') || v.includes('none')) return 'Anonymous — no session required.';
  return 'Authentication tier';
}

export default function ApiDocs() {
  const { data, loading, error, refetch } = useWidgetData('api-docs', () => ({}));
  const [filter, setFilter] = createSignal('');

  const endpoints = createMemo(() => {
    const list = data()?.endpoints || [];
    const q = filter().trim().toLowerCase();
    if (!q) return list;
    return list.filter(ep =>
      (ep.path || '').toLowerCase().includes(q) ||
      (ep.method || '').toLowerCase().includes(q) ||
      (ep.description || '').toLowerCase().includes(q),
    );
  });

  return (
    <div class="v2-widget">
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">API Documentation</span>
        <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
          title="Refresh" onClick={() => refetch()}>↻</button>
      </div>
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-widget__scroll" style={{ padding: '0.5rem 0.75rem' }}>
            <div style={{ 'margin-bottom': '0.75rem' }}>
              <div style={{
                'font-size': 'var(--fs-md)',
                'font-weight': '600',
                'font-family': 'var(--font-display, var(--font-mono))',
                'letter-spacing': '0.05em',
                'text-transform': 'uppercase',
                color: 'var(--t-green)',
              }}>
                {data()?.name}
                <span title="Spec version" style={{
                  color: 'var(--t-dim)',
                  'font-size': 'var(--fs-xs)',
                  'font-family': 'var(--font-mono)',
                  'letter-spacing': '0',
                  'text-transform': 'none',
                  'margin-left': '0.4rem',
                }}>
                  v{data()?.version}
                </span>
              </div>
              <div style={{ color: 'var(--t-dim)', 'font-size': 'var(--fs-xs)' }}>
                <span title="The shared prefix prepended to every endpoint path below.">Base URL:</span>{' '}
                <code style={{ color: 'var(--t-cyan)' }}>{data()?.baseUrl}</code>
                {' · '}
                <span title="Per-IP request budget. Exceeding it returns 429 Too Many Requests.">Rate limit:</span>{' '}
                <span style={{ color: 'var(--gold, #ffc857)' }}>{data()?.rateLimit}</span>
              </div>
              <div title="How callers prove who they are. Endpoints under the user/admin tiers reject anonymous requests with 401."
                style={{ color: 'var(--t-dim)', 'font-size': 'var(--fs-xs)', 'margin-top': '0.25rem' }}>
                {data()?.authentication}
              </div>
            </div>

            <input
              type="text"
              class="form-input"
              placeholder="Filter by path / method / description…"
              value={filter()}
              onInput={e => setFilter(e.target.value)}
              style={{ width: '100%', 'margin-bottom': '0.5rem' }}
            />

            <For each={endpoints()}>{ep => (
              <div style={{
                margin: '0 0 0.6rem',
                padding: '0.5rem 0.6rem',
                border: '1px solid var(--border, rgba(255,255,255,0.08))',
                'border-radius': 'var(--radius, 6px)',
                background: 'rgba(0,0,0,0.18)',
              }}>
                <div style={{ display: 'flex', 'align-items': 'center', gap: '0.5rem' }}>
                  <span title={methodHint(ep.method)} style={{
                    'font-family': 'var(--font-mono)',
                    'font-weight': 'bold',
                    color: methodColour(ep.method),
                    'min-width': '54px',
                  }}>{(ep.method || '').toUpperCase()}</span>
                  <code title="Endpoint path — append to the Base URL above."
                    style={{ color: 'var(--t-white)', 'font-size': 'var(--fs-sm)' }}>{ep.path}</code>
                  <span title={authHint(ep.auth)} style={{
                    color: authColour(ep.auth),
                    'font-size': 'var(--fs-xs)',
                    'font-family': 'var(--font-mono)',
                    'text-transform': 'uppercase',
                    'letter-spacing': '0.05em',
                    'margin-left': 'auto',
                  }}>{ep.auth}</span>
                </div>
                <Show when={ep.description}>
                  <div style={{ color: 'var(--t-secondary)', 'font-size': 'var(--fs-xs)', 'margin-top': '0.2rem' }}>
                    {ep.description}
                  </div>
                </Show>
                <Show when={ep.params}>
                  <div style={{ 'font-size': 'var(--fs-xs)', color: 'var(--t-dim)', 'margin-top': '0.25rem' }}>
                    <span title="Query-string parameters appended to the URL after a '?'." style={{ 'font-weight': '600', 'text-transform': 'uppercase', 'letter-spacing': '0.05em' }}>Params</span>
                    <For each={Object.entries(ep.params || {})}>{([k, v]) => (
                      <div style={{ 'margin-left': '0.75rem' }}>
                        <code title={`Parameter name — pass as ?${k}=…`} style={{ color: 'var(--neon)' }}>{k}</code> — {v}
                      </div>
                    )}</For>
                  </div>
                </Show>
                <Show when={ep.body}>
                  <div style={{ 'font-size': 'var(--fs-xs)', color: 'var(--t-dim)', 'margin-top': '0.25rem' }}>
                    <strong title="JSON request body fields. Send as application/json.">Body:</strong>{' '}
                    <For each={Object.entries(ep.body || {})}>{([k, v]) => (
                      <span style={{ 'margin-right': '0.4rem' }}>
                        <code title={`Body field — supplied as the JSON key "${k}".`} style={{ color: 'var(--neon)' }}>{k}</code>: {v}
                      </span>
                    )}</For>
                  </div>
                </Show>
              </div>
            )}</For>

            <Show when={endpoints().length === 0}>
              <div class="empty-state" style={{ padding: '1rem', 'text-align': 'center' }}>
                No endpoints match your filter.
              </div>
            </Show>
          </div>
        </Show>
      </Show>
    </div>
  );
}
